import { useState, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import CircularProgress from "@mui/material/CircularProgress";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { listClientProfiles } from "services/tenantUserAdminService";
import { TC } from "../../../tokens/text";

const COLORS = ["#0075ff", "#4caf50"];
const RADIAN = Math.PI / 180;

function CustomLabel({ cx, cy, midAngle, innerRadius, outerRadius, value }) {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: "0.85rem", fontWeight: 700 }}>
      {value}
    </text>
  );
}

export default function ClientBreakdownWidget({ tenantId }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    listClientProfiles({ tenantId })
      .then((res) => setProfiles((res.clientProfiles || []).filter(p => p.status !== "archived")))
      .catch(() => setProfiles([]))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const companies   = profiles.filter((p) => p.clientType !== "individual").length;
  const individuals = profiles.filter((p) => p.clientType === "individual" && !p.linkedCompanyClientId).length;
  const total       = companies + individuals;

  const pieData = [
    { name: "Companies",   value: companies },
    { name: "Individuals", value: individuals },
  ].filter((d) => d.value > 0);

  if (loading) {
    return (
      <VuiBox display="flex" justifyContent="center" alignItems="center" height="100%" py={4}>
        <CircularProgress size={28} />
      </VuiBox>
    );
  }

  if (total === 0) {
    return (
      <VuiBox display="flex" justifyContent="center" alignItems="center" height="100%" py={4}>
        <VuiTypography color="text" variant="button">No data</VuiTypography>
      </VuiBox>
    );
  }

  return (
    <VuiBox p={1} display="flex" flexDirection="column" alignItems="center" height="100%">
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={pieData} cx="50%" cy="50%" outerRadius={75}
            dataKey="value" labelLine={false} label={CustomLabel}>
            {pieData.map((_, i) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ background: "#0f1535", border: "1px solid #d8e6f7", borderRadius: 8 }}
            itemStyle={{ color: TC.primary }}
          />
          <Legend formatter={(v) => (
            <span style={{ color: TC.midtone, fontSize: "0.75rem" }}>{v}</span>
          )} />
        </PieChart>
      </ResponsiveContainer>
    </VuiBox>
  );
}

